import React from 'react';
import { SUPPORT_EMAIL } from 'apps/sigma2-ui/src/constants/site-meta';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { FaNeedMoreDataIconSvg } from 'apps/sigma2-ui/src/components/svg/FaNeedMoreDataIcon';
import { FaUpgradeIconSvg } from 'apps/sigma2-ui/src/components/svg/FaUpgradeIcon';
import { MyFocusAreaDefinition } from 'libs/end-to-end/src/my-focus-area-definition';

export const FocusAreaPlaceholder = (props: {
  focusArea: MyFocusAreaDefinition;
  status: 'empty' | 'disabled';
}) => {
  const { focusArea, status } = props;
  const language = useLanguage();
  const focusAreaName = focusArea.displayName;

  const callouts: Record<'empty' | 'disabled', string> = {
    empty: language.focusAreaEmptyCallout(),
    disabled: language.focusAreaDisabledCallout(),
  };

  const titles: Record<'empty' | 'disabled', string> = {
    empty: language.focusAreaEmptyTitle({ focusAreaName }),
    disabled: language.focusAreaDisabledTitle({ focusAreaName }),
  };

  const texts: Record<'empty' | 'disabled', string> = {
    empty: language.focusAreaEmptyText(),
    disabled: language.focusAreaDisabledText(),
  };

  const buttons: Record<'empty' | 'disabled', string> = {
    empty: language.focusAreaEmptyButton(),
    disabled: language.focusAreaDisabledButton(),
  };

  const emailSubjects: Record<'empty' | 'disabled', string> = {
    empty: language.focusAreaEmptyEmailSubject({
      focusAreaName,
    }),
    disabled: language.focusAreaDisabledEmailSubject({
      focusAreaName,
    }),
  };

  return (
    <div className="flex flex-row gap-5 items-center bg-white p-[40px] rounded-lg shadow-12px mt-24">
      <div className="flex-1 flex flex-row">
        <div className="flex flex-col gap-5">
          <div className="flex flex-row gap-2">
            {status === 'disabled' ? (
              <FaUpgradeIconSvg
                svg={{ className: 'w-6 h-6 block text-secondary' }}
              />
            ) : (
              <FaNeedMoreDataIconSvg
                svg={{ className: 'w-6 h-6 block text-secondary' }}
              />
            )}
            <p className="font-sans text-sm leading-6 text-gray-800">
              {callouts[status]}
              {status === 'disabled' && (
                <>
                  {' '}
                  <strong className="font-semibold">{focusAreaName}</strong>
                </>
              )}
            </p>
          </div>
          <p className="font-sans text-2xl leading-[48px] font-bold">
            {titles[status]}
          </p>
          <p className="font-sans text-lg leading-[30px] font-normal text-gray-800">
            {texts[status]}
          </p>
          <div className="flex flex-row">
            <LinkButton
              variant={LegacyButtonVariant.ANALYZE_BUTTON}
              data-qa="placeholderBtn"
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                emailSubjects[status],
              )}`}
              className="font-sans relative cursor-default w-48 text-sm whitespace-nowrap self-center text-center"
            >
              {buttons[status]}
            </LinkButton>
          </div>
        </div>
      </div>
      <div className="flex flex-col flex-1">
        <img
          className="text-primary"
          src={
            status === 'disabled'
              ? 'images/upgrade_graphic.svg'
              : 'images/need_more_data_graphic.svg'
          }
        />
      </div>
    </div>
  );
};
